import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Monitor } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/components/ui-kit";
import { PortalPWA } from "@/components/PortalPWA";
import api, { API } from "@/lib/api";
import CertifiedBadge from "@/components/CertifiedBadge";

// All portals share one responsive desktop-app shell: full-width console on desktop,
// collapses cleanly on tablet/phone, and installs as a mobile app (PortalPWA).
const META = {
  pro: { name: "PranIQ Clinician", tag: "Clinician app", maxw: "max-w-none w-full" },
  patient: { name: "PranIQ Patient", tag: "Patient app", maxw: "max-w-3xl" },
  admin: { name: "PranIQ Admin", tag: "Admin console", maxw: "max-w-[1440px]" },
  ops: { name: "PranIQ Operations", tag: "Operations console", maxw: "max-w-[1440px]" },
};

export default function PortalShell({ portal, children }) {
  const m = META[portal];
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const out = () => { logout(); nav("/"); };
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBroken, setPhotoBroken] = useState(false);
  const [comp, setComp] = useState(null);

  useEffect(() => {
    if (portal !== "pro" || user?.role !== "physiotherapist") return;
    api.get("/pro/compliance").then((r) => setComp(r.data)).catch(() => {});
  }, [portal, user?.role]);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/users/${user.id}/documents`).then((r) => {
      const ph = (r.data || []).find((d) => d.doc_type === "photo" && (d.content_type || "").startsWith("image/"));
      if (ph) setPhotoUrl(`${API}/documents/${ph.id}/file?auth=${localStorage.getItem("praniq_token")}`);
    }).catch(() => {});
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <header className="h-16 bg-[#0A1B33] text-white flex items-center gap-3 px-4 sm:px-7 sticky top-0 z-10 border-b-2 border-[#DAA520]/50">
        <Logo height={20} variant="light" />
        <div className="leading-tight min-w-0">
          <div className="font-display font-bold text-[15px] truncate">{m.name}</div>
          <div className="text-[10px] opacity-70 flex items-center gap-1"><Monitor size={10} /> {m.tag}</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center font-bold text-[13px] font-display shrink-0 overflow-hidden" data-testid="portal-photo">
            {photoUrl && !photoBroken
              ? <img src={photoUrl} alt={user?.name || ""} className="w-full h-full object-cover" onError={() => setPhotoBroken(true)} />
              : initials(user?.name || "A")}
          </div>
          <span className="text-[13px] font-semibold hidden sm:block">{user?.name}</span>
          <CertifiedBadge comp={comp} size={18} light testid="pro-header-certified" />
          <button onClick={out} data-testid="portal-logout" className="pq-btn pq-btn-sm bg-white/10 text-white border-white/20 hover:bg-white/20"><LogOut size={14} /> <span className="hidden sm:inline">Sign out</span></button>
        </div>
      </header>
      <main className={`px-4 sm:px-6 lg:px-7 py-6 ${m.maxw} mx-auto`}><PortalPWA portal={portal} />{children}</main>
    </div>
  );
}
